import type { ZodEffects } from 'zod';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import parsePhoneNumber from 'libphonenumber-js';
import { locales, type Locales } from '@/js/data/locales';
import { detectLocaleByHtml } from '@/js/utils/browser';
import { replaceAbstractText } from '@/js/utils/i18n';

export type FormSchema = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: ZodEffects<any>;
};

/**
 * @doc 正規表現
 */
const RegEx = {
  not_url: new RegExp(
    /^(?!((https?:)?\/\/[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#\u3000-\u30FE\u4E00-\u9FA0\uFF01-\uFFE3]))+/,
    'g',
  ),
  kana: /^[\u3040-\u309F]+$/, // カタカナのみにする場合 -> /[\u30A1-\u30F4\u30F7-\u30FA\u30FD\u30FE]+/
  number: /^[0-9]+$/,
  date: /^[0-9]{4}\/[0-9]{2}\/[0-9]{2}$/,
};

/**
 * @doc 文字数制限
 */
const LIMITS = {
  name: 30,
  kana: 40,
  entityName: 50, // 団体名・お名前
  phone: 15,
  email: 60,
  url: 100,
  body: 3000,
};

/**
 * @doc スキーマの定義
 * debug時は、zodのメソッドの transform() 等を使うと見やすい
 * @ref チェックボックス周りのリファレンス
    - https://github.com/airjp73/remix-validated-form/blob/main/packages/remix-validated-form/src/internal/logic/getCheckboxChecked.ts
    - https://github.com/colinhacks/zod/discussions/1525#discussioncomment-3981862
 */
const { locale } = detectLocaleByHtml();
const MESSAGES = (locales as any)[locale]?.MESSAGES as Locales['MESSAGES'];

const CONTACT_SCHEMA = z.object({
  requestType: zfd.repeatable(
    z.array(
      zfd.text(
        z
          .string({ required_error: MESSAGES.NOT_ENTERED.requestType })
          .min(1, { message: MESSAGES.NOT_ENTERED.requestType }),
      ),
    ),
  ),
  nameLast: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.nameLast })
      .min(1, { message: MESSAGES.NOT_ENTERED.nameLast })
      .max(LIMITS.name, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.name, LIMITS.name, '%{n}') }),
  ),
  nameFirst: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.nameFirst })
      .min(1, { message: MESSAGES.NOT_ENTERED.nameFirst })
      .max(LIMITS.name, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.name, LIMITS.name, '%{n}') }),
  ),
  ...(locale === 'ja'
    ? {
        kana: zfd.text(
          z
            .string({ required_error: MESSAGES.NOT_ENTERED.kana })
            .regex(RegEx.kana, MESSAGES.INCORRECT_FORMAT.kana)
            .min(1, { message: MESSAGES.NOT_ENTERED.kana })
            .max(LIMITS.kana, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.kana, LIMITS.kana, '%{n}') }),
        ),
      }
    : {}),
  phone: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.phone })
      .max(LIMITS.phone, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.phone, LIMITS.phone, '%{n}') })
      .refine(
        (value) => {
          const phoneNumber = parsePhoneNumber(value, 'JP');
          if (phoneNumber) {
            return phoneNumber.isValid();
          } else {
            return false;
          }
        },
        () => ({ message: MESSAGES.INCORRECT_FORMAT.phone }),
      ),
  ),
  email: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.email })
      .email({ message: MESSAGES.INCORRECT_FORMAT.email })
      .max(LIMITS.email, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.email, LIMITS.email, '%{n}') }),
  ),
  body: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.body })
      .max(LIMITS.body, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.body, LIMITS.body, '%{n}') }),
  ),
  // agree: zfd
  //   .text()
  //   .transform((val) => {
  //     console.log(val);
  //     return val === 'true' || val === 'on' || val === '1';
  //   })
  //   .refine((val) => val === true, { message: MESSAGES.MUST_BE_TRUE.privacy }),
});

const CONTACT_FOUNDATION_SCHEMA = z.object({
  // 団体名・お名前
  entityName: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.entityName })
      .min(1, { message: MESSAGES.NOT_ENTERED.entityName })
      .max(LIMITS.name, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.name, LIMITS.name, '%{n}') }),
  ),
  email: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.email })
      .email({ message: MESSAGES.INCORRECT_FORMAT.email })
      .max(LIMITS.email, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.email, LIMITS.email, '%{n}') }),
  ),
  body: zfd.text(
    z
      .string({ required_error: MESSAGES.NOT_ENTERED.body })
      .max(LIMITS.body, { message: replaceAbstractText(MESSAGES.MAX_LENGTH.body, LIMITS.body, '%{n}') }),
  ),
});

/**
 * @doc スキーマセットをexport
 */
const schemaMap = {
  // お問い合わせ種別
  contact_type: CONTACT_SCHEMA.shape.requestType,
  // お名前（姓）
  name_last: CONTACT_SCHEMA.shape.nameLast,
  // お名前（名）
  name_first: CONTACT_SCHEMA.shape.nameFirst,
  ...(locale === 'ja'
    ? {
        // カナ
        name_kana: CONTACT_SCHEMA.shape.kana,
      }
    : {}),
  // 電話番号
  phone: CONTACT_SCHEMA.shape.phone,
  // メール
  email: CONTACT_SCHEMA.shape.email,
  // お問い合わせ内容
  contact_body: CONTACT_SCHEMA.shape.body,
  // // 同意
  // agree: CONTACT_SCHEMA.shape.agree,
};

const schemaMapFoundation = {
  // 団体名・お名前
  entity_name: CONTACT_FOUNDATION_SCHEMA.shape.entityName,
  // メール
  email: CONTACT_FOUNDATION_SCHEMA.shape.email,
  // お問い合わせ内容
  contact_body: CONTACT_FOUNDATION_SCHEMA.shape.body,
  // 添付ファイルはFdForm.jsで別途バリデーション
};

const schemaKeysById = {
  all: [...Object.keys(schemaMap)], // コーポレート
  foundation: [...Object.keys(schemaMapFoundation)], // 財団
};

const schemas: FormSchema = Object.keys(schemaKeysById).reduce((acc, cur) => {
  const schemaSet = {};

  // @ts-ignore
  schemaKeysById[cur].forEach((key) => {
    // フォームタイプに応じて適切なスキーママップを選択
    const sourceSchema = cur === 'foundation' ? schemaMapFoundation : schemaMap;
    // @ts-ignore
    schemaSet[key] = sourceSchema[key];
  });

  // @ts-ignore
  acc[cur] = zfd.formData(schemaSet);

  // @ts-ignore
  return acc;
}, {});

export default schemas;
export { schemaKeysById, MESSAGES };
